import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, Headphones } from 'lucide-react';
import { voiceSamples } from '../data/sampleStory.js';

/**
 * VoiceSamplesPlayer - Interactive voice sample grid for the landing page
 * Lets visitors hear each of the 6 AI narration voices
 */
export default function VoiceSamplesPlayer() {
  const [playingVoice, setPlayingVoice] = useState(null);
  const [audioError, setAudioError] = useState({});
  const audioRef = useRef(null);

  // Stop audio when switching voices or unmounting
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayPause = (voice) => {
    // If same voice is playing, pause it
    if (playingVoice === voice.id) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingVoice(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Create new audio for this voice
    const audio = new Audio(voice.audioUrl);
    audioRef.current = audio;

    audio.onended = () => {
      setPlayingVoice(null);
    };

    audio.onerror = () => {
      setAudioError((prev) => ({ ...prev, [voice.id]: true }));
      setPlayingVoice(null);
    };

    audio.play().then(() => {
      setPlayingVoice(voice.id);
    }).catch(() => {
      setAudioError((prev) => ({ ...prev, [voice.id]: true }));
    });
  };

  // Sample text that voices read (displayed for context)
  const sampleText = "Luna the little fox felt her heart racing as she looked at the tall mountain ahead...";

  return (
    <section className="py-16 section-amber">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="section-header mb-4">Stories That Come to Life</h2>
          <p className="text-[#5C534A] text-lg max-w-2xl mx-auto">
            6 unique AI voices bring every story to life. Click to hear each narrator.
          </p>
        </div>

        {/* Voice Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
          {voiceSamples.map((voice, index) => {
            const isPlaying = playingVoice === voice.id;
            const hasError = audioError[voice.id];

            return (
              <motion.div
                key={voice.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative storybook-card p-4 sm:p-6 cursor-pointer ${
                  isPlaying ? 'border-2 border-[#7B68EE] shadow-lg' : ''
                }`}
                onClick={() => !hasError && handlePlayPause(voice)}
                whileHover={{ y: -4 }}
              >
                {/* Voice Avatar/Icon */}
                <div
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: `${voice.color}20` }}
                >
                  {isPlaying ? (
                    <AudioWaveAnimation color={voice.color} />
                  ) : (
                    <Headphones className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: voice.color }} />
                  )}
                </div>

                {/* Voice Name */}
                <h3 className="text-center font-semibold text-[#3D3832] mb-1">
                  {voice.name}
                </h3>

                {/* Voice Description */}
                <p className="text-center text-sm text-[#8B8279] mb-3">
                  {voice.gender} • {voice.description}
                </p>

                {/* Play/Pause Button */}
                <button
                  className={`w-full flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all ${
                    isPlaying
                      ? 'bg-[#7B68EE]/10 text-[#7B68EE]'
                      : hasError
                      ? 'bg-[#E8DFD5] text-[#8B8279] cursor-not-allowed'
                      : 'bg-[#F5EDE3] text-[#5C534A] hover:bg-[#E8DFD5]'
                  }`}
                  disabled={hasError}
                >
                  {hasError ? (
                    'Sample unavailable'
                  ) : isPlaying ? (
                    <>
                      <Pause className="w-4 h-4" />
                      <span>Playing...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Play Sample</span>
                    </>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Sample Text Display */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-[#8B8279] mb-2">
            <Volume2 className="w-4 h-4" />
            <span>Sample text being read:</span>
          </div>
          <p className="text-[#5C534A] italic max-w-xl mx-auto">
            "{sampleText}"
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * AudioWaveAnimation - Animated audio visualization
 */
function AudioWaveAnimation({ color }) {
  return (
    <div className="flex items-end gap-0.5 h-6">
      {[1, 2, 3, 4, 5].map((bar) => (
        <motion.div
          key={bar}
          className="w-1 rounded-full"
          style={{ backgroundColor: color }}
          animate={{
            height: ['40%', '100%', '60%', '90%', '40%'],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: bar * 0.1,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}
